import React, { useState } from 'react';
import { FaWindows, FaAndroid, FaDownload, FaGooglePlay, FaFlask } from 'react-icons/fa';

const LauncherSection = () => {
  const [activeTab, setActiveTab] = useState('windows');
  
  const requirements = {
    windows: [
      { label: "OS", value: "Windows 10 (64bit)" },
      { label: "Processor", value: "Intel Core i5-6600k/AMD FX-6300" },
      { label: "RAM", value: "6GB" },
      { label: "Graphics Card", value: "From 2GB Video Memory" },
      { label: "Disk Space", value: "12 GB" }
    ],
    android: [
      { label: "OS", value: "Android 5 and above" },
      { label: "Processor", value: "Frequency from 1.2Ghz" },
      { label: "RAM", value: "4GB" },
      { label: "Disk Space", value: "8GB" }
    ]
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold gradient-text">
          CÀI ĐẶT LAUNCHER CỦA CHÚNG TÔI
        </h2>
      </div>
      
      {/* Content Container with positioning context */}
      <div className="relative mb-16">
        {/* Fixed size launcher card - removed fixed width */}
        <div className="launcher-card rounded-3xl bg-gradient-to-br from-dark/80 to-dark/40 backdrop-blur-md border border-white/10 shadow-xl w-full h-[602px] mx-auto overflow-hidden z-10 relative">
          <div className="flex flex-col h-full p-8 md:p-12">
            {/* System Requirements Label */}
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-white">System Requirements</h3>
            
            {/* Platform Tabs - Pill style */}
            <div className="inline-flex bg-dark/30 rounded-full p-1 self-start mb-8 relative z-50 pointer-events-auto">
              <button 
                className={`px-6 py-2 rounded-full text-md font-medium transition-all platform-tab ${
                  activeTab === 'windows' 
                    ? 'bg-white text-dark platform-tab-active' 
                    : 'text-light hover:bg-dark/20'
                }`}
                onClick={() => setActiveTab('windows')}
              >
                <span className="flex items-center">
                  <FaWindows className="mr-2" />
                  Windows
                </span>
              </button>
              
              <button 
                className={`px-6 py-2 rounded-full text-md font-medium transition-all platform-tab ${
                  activeTab === 'android' 
                    ? 'bg-white text-dark platform-tab-active' 
                    : 'text-light hover:bg-dark/20'
                }`}
                onClick={() => setActiveTab('android')}
              >
                <span className="flex items-center">
                  <FaAndroid className="mr-2" />
                  Android
                </span>
              </button>
            </div>
            
            {/* Content Section - Left side only */}
            <div className="w-full md:w-1/2 relative h-full z-20">
              {/* Windows content */}
              <div className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'windows' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                <div className="space-y-4 mb-8">
                  {requirements.windows.map((req, index) => (
                    <div key={index} className="flex">
                      <div className="w-1/3 text-light/70 text-lg">{req.label}</div>
                      <div className="w-2/3 text-white font-medium text-lg">{req.value}</div>
                    </div>
                  ))}
                </div>
                
                <a 
                  href="/downloads/psa-launcher-windows.exe" 
                  className="inline-flex items-center bg-accent-teal hover:bg-accent-teal/90 text-dark font-medium px-8 py-3 rounded-lg transition-all text-lg"
                >
                  <FaDownload className="mr-2" />
                  Download Game
                </a>
              </div>
              
              {/* Android content */}
              <div className={`absolute inset-0 transition-opacity duration-300 ${activeTab === 'android' ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                <div className="space-y-4 mb-8">
                  {requirements.android.map((req, index) => (
                    <div key={index} className="flex">
                      <div className="w-1/3 text-light/70 text-lg">{req.label}</div>
                      <div className="w-2/3 text-white font-medium text-lg">{req.value}</div>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.psa.launcher" 
                    className="inline-flex items-center bg-[#00C853] hover:bg-[#00C853]/90 text-white font-medium px-5 py-3 rounded-lg transition-all"
                  >
                    <FaGooglePlay className="mr-2" />
                    Play Market
                  </a>
                  
                  <a 
                    href="/downloads/psa-launcher-android.apk" 
                    className="inline-flex items-center bg-accent-teal hover:bg-accent-teal/90 text-dark font-medium px-5 py-3 rounded-lg transition-all"
                  >
                    <FaDownload className="mr-2" />
                    Direct Download
                  </a>
                  
                  <a 
                    href="/downloads/psa-launcher-android-test.apk" 
                    className="inline-flex items-center bg-amber-500 hover:bg-amber-500/90 text-dark font-medium px-5 py-3 rounded-lg transition-all"
                  >
                    <FaFlask className="mr-2" />
                    Test Version
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Absolute positioned images outside the card */}
        <div className="absolute top-0 right-0 z-20 h-full hidden md:block pointer-events-none" style={{ width: '50%' }}>
          {/* Windows Image with sliding animation */}
          <div 
            className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform pointer-events-none ${
              activeTab === 'windows' 
                ? 'translate-x-0 opacity-100' 
                : 'translate-x-full opacity-0'
            }`}
          >
            <img 
              src="https://psa.world/resources/images/psa_pc.png" 
              alt="Windows Launcher" 
              className="max-h-[700px] max-w-full object-contain pointer-events-none"
              style={{ transform: 'scale(1.25)' }}
            />
          </div>
          
          {/* Android Image with sliding animation */}
          <div 
            className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform pointer-events-none ${
              activeTab === 'android' 
                ? 'translate-x-0 opacity-100' 
                : '-translate-x-full opacity-0'
            }`}
          >
            <img 
              src="https://psa.world/resources/images/psa_android.png" 
              alt="Android Gameplay" 
              className="max-h-[700px] max-w-full object-contain pointer-events-none"
              style={{ transform: 'scale(1.05)' }}
            />
          </div>
        </div>
        
        {/* Mobile-only images */}
        <div className="mt-6 block md:hidden">
          <div className="relative h-[300px]">
            {/* Windows mobile image */}
            <div 
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform ${
                activeTab === 'windows' 
                  ? 'translate-x-0 opacity-100' 
                  : 'translate-x-full opacity-0'
              }`}
            >
              <img 
                src="https://psa.world/resources/images/psa_pc.png" 
                alt="Windows Launcher" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
            
            {/* Android mobile image */}
            <div 
              className={`absolute inset-0 flex items-center justify-center transition-all duration-500 transform ${
                activeTab === 'android' 
                  ? 'translate-x-0 opacity-100' 
                  : '-translate-x-full opacity-0'
              }`}
            >
              <img 
                src="https://psa.world/resources/images/psa_android.png" 
                alt="Android Gameplay" 
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LauncherSection;
